/*
  Googan Squad product page components (main-product blocks):
    <gs-product-header>   price, sale and NEW / RESERVE / Sold out badges follow the selected variant
    <gs-low-stock>        "Low inventory remaining" for low variants only
    <gs-rig>              "Complete the rig": the product plus matching add-ons in one tap
    <gs-variant-picker>   photo color swatches + size buttons (pack counts, water clarity filter),
                          plus the phone color row under the photos ([data-gsvp-rail])
  The picker tells the theme what was picked through the theme's own option-change event, so price,
  photos, stock, URL and Add to cart update exactly as they do with the theme's picker.
*/
(() => {
  const NEW_WINDOW_DAYS_BEFORE = 60; // a launch date up to this many days ahead also counts as NEW

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysSince = (s) => {
    if (!s) return null;
    const p = String(s).slice(0, 10).split('-');
    if (p.length < 3) return null;
    return Math.floor((today - new Date(+p[0], +p[1] - 1, +p[2])) / 864e5);
  };
  const isNewColor = (variants, colorOf, color, days) =>
    variants
      .filter((v) => colorOf(v) === color)
      .some((v) => {
        const n = daysSince(v.ld);
        return n !== null && n <= days && n >= -NEW_WINDOW_DAYS_BEFORE;
      });
  const isReserveColor = (variants, colorOf, color) =>
    /reserve/i.test(color) || variants.some((v) => colorOf(v) === color && /reserve/i.test(v.t));

  // Selected variant id: the product form's hidden input, else ?variant= in the URL.
  const currentVariantId = (scope, known) => {
    const root = scope.closest('product-info') || document;
    for (const input of root.querySelectorAll('form[action*="/cart/add"] [name="id"]')) {
      if (known(String(input.value))) return String(input.value);
    }
    const u = new URL(location.href).searchParams.get('variant');
    return u && known(u) ? u : null;
  };

  // Calls cb(variantId) whenever the theme switches variant.
  const onVariantChange = (scope, cb) => {
    if (window.FoxTheme?.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.variant?.id) cb(String(data.variant.id));
      });
    }
    const root = scope.closest('product-info') || document;
    root.addEventListener('change', (e) => {
      if (e.target?.name === 'id' && e.target.closest('form[action*="/cart/add"]')) cb(String(e.target.value));
    });
    window.addEventListener('popstate', () => {
      const u = new URL(location.href).searchParams.get('variant');
      if (u) cb(u);
    });
  };

  // Smooth scroll for in-page links (rating -> reviews, "Watch it swim" -> How to fish it).
  const headerOffset = () => {
    const h = document.querySelector('sticky-header, .shopify-section-header-sticky, .section-header, header');
    if (!h) return 0;
    const pos = getComputedStyle(h).position;
    const box = h.getBoundingClientRect();
    return (pos === 'sticky' || pos === 'fixed') && box.bottom > 0 ? box.bottom : 0;
  };
  const scrollToEl = (el) => {
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset()),
      behavior: calm ? 'auto' : 'smooth',
    });
  };
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-gs-scroll]');
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    scrollToEl(target);
    history.replaceState?.(null, '', link.getAttribute('href'));
  });

  /* ---------------- Product header ---------------- */
  if (!customElements.get('gs-product-header')) {
    customElements.define(
      'gs-product-header',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const D = JSON.parse(this.querySelector('[data-gph-json]').textContent);
          const byId = {};
          D.v.forEach((v) => (byId[String(v.id)] = v));
          const q = (s) => this.querySelector(s);
          const el = {
            now: q('[data-gph-now]'), was: q('[data-gph-was]'), save: q('[data-gph-save]'),
            row: q('[data-gph-badges]'), res: q('[data-gph-reserve]'), neu: q('[data-gph-new]'),
            out: q('[data-gph-out]'), main: q('.gph__chip--main'),
          };
          const colorOf = (v) => (D.ci > -1 ? v.o[D.ci] : v.t);
          let shown = null;
          const render = (id) => {
            const v = byId[String(id)];
            if (!v || shown === v.id) return;
            shown = v.id;
            const c = colorOf(v);
            const r = isReserveColor(D.v, colorOf, c);
            const n = isNewColor(D.v, colorOf, c, D.days);
            el.res.hidden = !r;
            el.neu.hidden = !n;
            el.out.hidden = v.a;
            el.row.hidden = !(el.main || r || n || !v.a);
            el.now.textContent = v.p;
            el.was.hidden = !v.c;
            el.was.textContent = v.c || '';
            el.save.hidden = !v.c;
            el.save.textContent = v.c ? v.s : '';
          };
          onVariantChange(this, render);
          render(currentVariantId(this, (id) => !!byId[id]) || D.sel);

          // Phones: sit above the photos (Nike / Tecovas style); desktop: back in the info column.
          const slot = this.closest('product-info')?.querySelector('[data-gs-mobile-head]');
          if (slot) {
            // On phones the inline script in snippets/gs-product-header already moved it (before first paint, so
            // the photos don't jump) and left this.gsHome as the marker of its desktop spot.
            const home = this.gsHome || document.createComment('gs-product-header');
            if (!this.gsHome) this.parentNode.insertBefore(home, this);
            const mq = window.matchMedia('(max-width: 767px)');
            const place = () => {
              if (mq.matches) { if (this.parentNode !== slot) slot.appendChild(this); }
              else if (home.parentNode && this.previousSibling !== home) home.parentNode.insertBefore(this, home.nextSibling);
            };
            mq.addEventListener('change', place);
            place();
          }

          const rating = q('[data-gph-rating]');
          rating?.addEventListener('click', (e) => {
            const hit = document.querySelector(
              '[id*="junip_product_review" i], [id*="junip-product-review" i], .junip-product-review, junip-product-review, .junip-product-review-widget'
            );
            const target = hit ? hit.closest('.shopify-section') || hit : null;
            if (!target) return;
            e.preventDefault();
            scrollToEl(target);
          });
        }
      }
    );
  }

  /* ---------------- Low stock notice ---------------- */
  if (!customElements.get('gs-low-stock')) {
    customElements.define(
      'gs-low-stock',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const low = new Set(this.dataset.low.split(',').filter(Boolean));
          const all = new Set(this.dataset.all.split(',').filter(Boolean));
          const update = (id) => {
            if (all.has(String(id))) this.hidden = !low.has(String(id));
          };
          onVariantChange(this, update);
          const id = currentVariantId(this, (x) => all.has(x));
          if (id) update(id);
        }
      }
    );
  }

  /* ---------------- Color + size picker ---------------- */
  if (!customElements.get('gs-variant-picker')) {
    customElements.define(
      'gs-variant-picker',
      class GSVariantPicker extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const D = (this.D = JSON.parse(this.querySelector('[data-gsvp-json]').textContent));
          this.V = D.v;
          this.CI = D.ci;
          this.N = D.opts.length;
          this.uid = 'gsvp' + Math.random().toString(36).slice(2, 8);
          this.productInfo = this.closest('product-info');
          this.rail = this.productInfo?.querySelector('[data-gsvp-rail]') || null;
          if (this.rail) this.classList.add('gsvp--has-rail');
          this.productInfo?.classList.add('gs-has-picker');

          const start = this.V.find((v) => v.id === D.sel) || this.V[0];
          this.state = { vals: start.o.slice(), water: 'all' };
          this.water = this.parseWater(D.water);
          this.pack = this.parsePack(D.pack);

          this.opts = [...this.querySelectorAll('[data-gsvp-opt]')].map((el) => ({
            el,
            list: el.querySelector('[data-gsvp-list]'),
            name: el.querySelector('[data-gsvp-name]'),
            meta: el.querySelector('[data-gsvp-meta]'),
            btns: [],
          }));
          this.build();
          this.querySelectorAll('[data-w]').forEach((b) =>
            b.addEventListener('click', () => {
              this.state.water = b.dataset.w;
              this.render(true);
              this.scrollRailToFirstMatch();
            })
          );

          onVariantChange(this, (id) => {
            const v = this.V.find((x) => String(x.id) === String(id));
            if (!v) return;
            this.state.vals = v.o.slice();
            this.render();
          });
          this.render();
          requestAnimationFrame(() => this.scrollRailToSelected(false));
        }

        /* ----- data helpers ----- */
        valuesOf(i) {
          return this.D.opts[i].map((o) => o.v);
        }
        exact(vals) {
          return this.V.find((v) => v.o.every((x, i) => x === vals[i]));
        }
        withVal(i, x) {
          const a = this.state.vals.slice();
          a[i] = x;
          return a;
        }
        best(i, x) {
          // closest variant that has value x for option i: in stock first, then most options in common
          const score = (v) => (v.a ? 100 : 0) + v.o.reduce((s, y, j) => s + (j !== i && y === this.state.vals[j] ? 10 - j : 0), 0);
          return this.V.filter((v) => v.o[i] === x).sort((a, b) => score(b) - score(a))[0];
        }
        colorOf(v) {
          return this.CI > -1 ? v.o[this.CI] : v.t;
        }
        imgFor(color) {
          const m = this.V.filter((v) => v.o[this.CI] === color && v.img);
          const same = m.find((v) => v.o.every((x, i) => i === this.CI || x === this.state.vals[i]));
          return (same || m[0] || {}).img || this.D.fb;
        }
        parseWater(w) {
          if (!w) return null;
          const out = {};
          Object.keys(w).forEach((k) => {
            out[k] = String(w[k] || '')
              .split(/[\n,]+/)
              .map((s) => s.trim())
              .filter(Boolean);
          });
          return out;
        }
        waterOf(color) {
          if (!this.water) return [];
          return Object.keys(this.water).filter((k) => this.water[k].includes(color));
        }
        // Pack counts from the "Package Includes" text:
        //   "Quantity of 7 Baits in 4" Style"  -> 4" button shows "7 per pack"
        //   "Quantity of 8 Baits"              -> every size shows "8 per pack"
        // Packs of 1 are not shown.
        parsePack(text) {
          const out = { bySize: {}, all: null };
          if (!text) return out;
          const decoder = document.createElement('textarea');
          decoder.innerHTML = String(text); // the field text arrives HTML-encoded (&quot;)
          const t = decoder.value.replace(/[”″“]/g, '"');
          const sized = /quantity of\s+(\d+)\s*(?:[a-z]+\s+){0,2}in\s+(?:the\s+)?([\d.\/]+)\s*(?:"|in\b|inch)/gi;
          let m;
          while ((m = sized.exec(t))) out.bySize[m[2]] = +m[1];
          if (!Object.keys(out.bySize).length) {
            const one = /quantity of\s+(\d+)/i.exec(t);
            if (one) out.all = +one[1];
          }
          return out;
        }
        packFor(x) {
          const key = String(x).replace(/["”″\s]|inch(es)?/gi, '');
          const n = this.pack.bySize[key] ?? this.pack.all;
          return n && n > 1 ? n + ' per pack' : '';
        }

        /* ----- build buttons ----- */
        makeColorButton(i, x, inRail) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = inRail ? 'gs-rail__sw' : 'gsvp-sw';
          b.setAttribute('role', 'radio');
          b.dataset.x = x;
          b.id = `${this.uid}-${inRail ? 'r' : 'c'}${this.valuesOf(i).indexOf(x)}`;
          b.title = x;
          const n = isNewColor(this.V, (v) => this.colorOf(v), x, this.D.days);
          const r = isReserveColor(this.V, (v) => this.colorOf(v), x);
          let tags = '';
          if (r) tags += '<span class="gsvp-tag gsvp-tag--res gsvp-tag--top">RESERVE</span>';
          if (n) tags += `<span class="gsvp-tag gsvp-tag--new ${r ? 'gsvp-tag--bot' : 'gsvp-tag--top'}">NEW</span>`;
          b.setAttribute('aria-label', x + (r ? ', reserve, limited edition' : '') + (n ? ', new' : ''));
          b.innerHTML = `<img src="${this.imgFor(x)}" alt="" loading="lazy" width="120" height="120">${tags}`;
          if (!inRail) {
            const O = this.opts[i];
            b.addEventListener('mouseenter', () => (O.name.textContent = x));
            b.addEventListener('mouseleave', () => (O.name.textContent = this.state.vals[i]));
          }
          b.addEventListener('click', () => this.choose(i, x, b));
          return b;
        }

        build() {
          this.railBtns = [];
          this.opts.forEach((O, i) => {
            const values = this.valuesOf(i);
            if (i !== this.CI) {
              const notes = values.map((x) => this.packFor(x));
              if (!notes.some(Boolean)) O.list.classList.add('gsvp-pills--compact');
              if (!notes.some(Boolean) && values.every((x) => String(x).length <= 4)) O.list.classList.add('gsvp-pills--short');
            }
            values.forEach((x, k) => {
              let b;
              if (i === this.CI) {
                b = this.makeColorButton(i, x, false);
              } else {
                b = document.createElement('button');
                b.type = 'button';
                b.className = 'gsvp-pill';
                b.setAttribute('role', 'radio');
                b.dataset.x = x;
                b.id = `${this.uid}-o${i}-${k}`;
                b.setAttribute('aria-label', x);
                b.innerHTML = '<b></b><small></small>';
                b.querySelector('b').textContent = x;
                b.addEventListener('click', () => this.choose(i, x, b));
              }
              O.list.appendChild(b);
              O.btns.push(b);
            });
            O.list.addEventListener('keydown', (e) => this.onKey(e, O.btns, i));
          });
          if (this.rail && this.CI > -1) {
            this.valuesOf(this.CI).forEach((x) => {
              const b = this.makeColorButton(this.CI, x, true);
              this.rail.appendChild(b);
              this.railBtns.push(b);
            });
            this.rail.addEventListener('keydown', (e) => this.onKey(e, this.railBtns, this.CI));
            this.initRailMore();
          }
        }

        onKey(e, btns, i) {
          const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (!keys[e.key]) return;
          e.preventDefault();
          const live = btns.filter((b) => !b.disabled && !b.hidden);
          const k = live.indexOf(document.activeElement);
          if (k < 0) return;
          const nb = live[(k + keys[e.key] + live.length) % live.length];
          nb.focus();
          this.choose(i, nb.dataset.x, nb);
        }

        /* ----- render ----- */
        render(pop) {
          const cur = this.exact(this.state.vals);
          if (this.water) this.dataset.water = this.state.water;
          this.opts.forEach((O, i) => {
            if (i === this.CI) {
              const match = this.water && this.state.water !== 'all' ? this.state.water : null;
              let hits = 0;
              const paint = (b, k) => {
                const x = b.dataset.x;
                const v = this.exact(this.withVal(i, x));
                const sel = x === this.state.vals[i];
                b.setAttribute('aria-checked', sel ? 'true' : 'false');
                b.tabIndex = sel ? 0 : -1;
                const on = !match || this.waterOf(x).includes(match);
                b.classList.toggle('is-dim', !on);
                b.classList.toggle('is-rec', !!match && on);
                b.classList.toggle('is-na', !v);
                b.classList.toggle('is-out', !!v && !v.a);
                b.title = v
                  ? x
                  : `${x} (only in ${this.V.filter((w) => w.o[this.CI] === x)
                      .map((w) => w.o.filter((_, j) => j !== this.CI).join(' / '))
                      .join(' & ')})`;
                b.classList.remove('is-pop');
                if (pop && match && on) {
                  void b.offsetWidth;
                  b.style.animationDelay = k * 22 + 'ms';
                  b.classList.add('is-pop');
                }
                const im = b.querySelector('img');
                const src = this.imgFor(x);
                if (im.getAttribute('src') !== src) im.src = src;
                return on && !!v;
              };
              O.btns.forEach((b, k) => {
                if (paint(b, k)) hits++;
              });
              this.railBtns.forEach((b, k) => paint(b, k));
              O.meta.textContent = match ? `${hits} best for ${match} water` : '';
              O.meta.classList.toggle('is-water', !!match);
              this.querySelectorAll('[data-w]').forEach((b) =>
                b.setAttribute('aria-pressed', b.dataset.w === this.state.water ? 'true' : 'false')
              );
              const note = this.querySelector('[data-gsvp-note]');
              if (note) note.hidden = !isReserveColor(this.V, (v) => this.colorOf(v), this.state.vals[i]);
            } else {
              O.btns.forEach((b) => {
                const x = b.dataset.x;
                const v = this.exact(this.withVal(i, x));
                const sel = x === this.state.vals[i];
                b.setAttribute('aria-checked', sel ? 'true' : 'false');
                b.tabIndex = sel ? 0 : -1;
                b.disabled = !v;
                b.classList.toggle('is-out', !!v && !v.a);
                b.title = v ? (v.a ? '' : 'Sold out') : 'Not offered' + (this.CI > -1 ? ' in ' + this.state.vals[this.CI] : '');
                b.querySelector('small').textContent = !v ? 'Not offered' : !v.a ? 'Sold out' : this.packFor(x);
              });
              const offered = this.valuesOf(i).filter((x) => !!this.exact(this.withVal(i, x)));
              if (cur && !cur.a) O.meta.textContent = this.CI > -1 ? 'Sold out in this color' : 'Sold out';
              else if (this.CI > -1 && offered.length && offered.length < this.valuesOf(i).length)
                O.meta.textContent = `${this.state.vals[this.CI]} only comes in ${offered.join(' & ')}`;
              else O.meta.textContent = '';
            }
            O.name.textContent = this.state.vals[i];
          });
          // Products with colors only (no size option): show the pack count beside the color name.
          if (this.CI > -1 && this.N === 1 && !(this.water && this.state.water !== 'all')) {
            this.opts[this.CI].meta.textContent = this.packFor('');
          }
        }

        /* ----- phone color row ----- */
        // Arrow at the right end of the row while more colors are off-screen; tap to slide over.
        initRailMore() {
          const more = this.rail.parentElement.querySelector('[data-gsvp-rail-more]');
          if (!more) return;
          const r = this.rail;
          let raf = 0;
          const update = () => {
            raf = 0;
            more.hidden = !(r.scrollWidth - r.clientWidth - r.scrollLeft > 8);
          };
          const queue = () => raf || (raf = requestAnimationFrame(update));
          r.addEventListener('scroll', queue, { passive: true });
          window.addEventListener('resize', queue, { passive: true });
          more.addEventListener('click', () => r.scrollBy({ left: r.clientWidth * 0.75, behavior: 'smooth' }));
          queue();
        }

        scrollRail(btn, smooth) {
          if (!this.rail || !btn || !this.rail.offsetParent) return;
          const r = this.rail;
          const left = btn.offsetLeft - (r.clientWidth - btn.offsetWidth) / 2;
          r.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
        }
        scrollRailToSelected(smooth = true) {
          this.scrollRail(this.railBtns.find((b) => b.getAttribute('aria-checked') === 'true'), smooth);
        }
        scrollRailToFirstMatch() {
          if (this.state.water === 'all') return this.scrollRailToSelected();
          this.scrollRail(this.railBtns.find((b) => b.classList.contains('is-rec')), true);
        }

        /* ----- choosing ----- */
        choose(i, x, btn) {
          const target = this.exact(this.withVal(i, x)) || this.best(i, x);
          if (!target) return;
          const changed = target.o.some((val, j) => val !== this.state.vals[j]);
          this.state.vals = target.o.slice();
          this.render();
          if (i === this.CI) this.scrollRailToSelected();
          if (!changed) return;
          const ids = target.o.map((val, j) => (this.D.opts[j].find((o) => o.v === val) || {}).id).filter(Boolean);
          if (window.FoxTheme?.pubsub && this.productInfo && ids.length === this.N) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: { event: { target: btn }, target: btn, selectedOptionValues: ids },
            });
          } else {
            const u = new URL(location.href);
            u.searchParams.set('variant', target.id);
            location.href = u.toString();
          }
        }
      }
    );
  }
  /* ---------------- Shared: add items to the cart and open the cart drawer like the theme does ---------------- */
  const refreshCart = async () => {
    const cart = await (await fetch(`${window.FoxTheme?.routes?.cart_url || '/cart'}.js`)).json();
    window.FoxTheme?.pubsub?.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
  };
  const formatMoney = (cents, format) => {
    const n = (cents / 100).toFixed(2);
    const withCommas = (x) => x.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === 'amount_no_decimals') return withCommas(String(Math.round(cents / 100)));
      if (k === 'amount_with_comma_separator') return n.replace('.', ',');
      return withCommas(n.split('.')[0]) + '.' + n.split('.')[1];
    });
  };

  /* ---------------- Complete the rig ---------------- */
  if (!customElements.get('gs-rig')) {
    customElements.define(
      'gs-rig',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const D = JSON.parse(this.querySelector('[data-rig-json]').textContent);
          this.D = D;
          this.mainId = String(D.sel);
          this.btn = this.querySelector('[data-rig-add]');
          this.msg = this.querySelector('[data-rig-msg]');
          const main = this.querySelector('[data-main]');
          this.mainEl = { price: main.querySelector('[data-rig-price]'), opt: main.querySelector('[data-rig-main-opt]'), img: main.querySelector('[data-rig-img]') };

          this.querySelectorAll('[data-rig-item]:not([data-main])').forEach((li) => {
            const box = li.querySelector('input[type="checkbox"]');
            const sel = li.querySelector('select[data-rig-variant]');
            li.classList.toggle('is-off', !box.checked);
            box.addEventListener('change', () => { li.classList.toggle('is-off', !box.checked); this.total(); });
            sel?.addEventListener('change', () => {
              const o = sel.selectedOptions[0];
              const price = li.querySelector('[data-rig-price]');
              price.dataset.cents = o.dataset.price;
              price.textContent = o.dataset.priceText;
              this.total();
            });
          });
          onVariantChange(this, (id) => {
            const v = D.v[String(id)];
            if (!v) return;
            this.mainId = String(id);
            this.mainEl.price.textContent = v.p;
            if (this.mainEl.opt && this.mainEl.opt.textContent) this.mainEl.opt.textContent = v.t;
            if (this.mainEl.img && v.img) this.mainEl.img.src = v.img;
            this.total();
          });
          const first = currentVariantId(this, (id) => !!D.v[id]);
          if (first) this.mainId = first;
          this.btn.addEventListener('click', () => this.add());
          this.total();
        }

        items() {
          const out = [];
          const mv = this.D.v[this.mainId];
          if (mv && mv.a) out.push({ id: Number(this.mainId), quantity: 1, cents: mv.c });
          this.querySelectorAll('[data-rig-item]:not([data-main])').forEach((li) => {
            if (!li.querySelector('input[type="checkbox"]').checked) return;
            const v = li.querySelector('[data-rig-variant]');
            out.push({ id: Number(v.value), quantity: 1, cents: Number(li.querySelector('[data-rig-price]').dataset.cents) });
          });
          return out;
        }

        total() {
          const items = this.items();
          const sum = items.reduce((t, i) => t + i.cents, 0);
          this.querySelector('[data-rig-total]').textContent = items.length ? '· ' + formatMoney(sum, this.D.money) : '';
          this.querySelector('[data-rig-label]').textContent = items.length > 1 ? `Add ${items.length} to cart` : items.length ? 'Add to cart' : 'Select items';
          this.btn.disabled = !items.length;
        }

        async add() {
          const items = this.items().map(({ id, quantity }) => ({ id, quantity }));
          if (!items.length) return;
          this.btn.disabled = true;
          this.btn.classList.add('is-loading');
          this.msg.hidden = true;
          try {
            const res = await fetch(`${window.FoxTheme?.routes?.cart_add_url || '/cart/add'}.js`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
            });
            const data = await res.json();
            if (data.status) throw new Error(data.description || data.message);
            await refreshCart();
          } catch (e) {
            this.msg.textContent = e.message || 'Something went wrong. Please try again.';
            this.msg.hidden = false;
          } finally {
            this.btn.classList.remove('is-loading');
            this.total();
          }
        }
      }
    );
  }

  /* ---------------- 5-star review quotes (Junip) ---------------- */
  if (!customElements.get('gs-review-quotes')) {
    customElements.define(
      'gs-review-quotes',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const go = () => this.load().catch(() => {});
          const start = () => {
            if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 2500 });
            else setTimeout(go, 1200);
          };
          // Desktop only: on mobile nothing is fetched unless the window is widened
          const mql = window.matchMedia('(min-width: 768px)');
          if (mql.matches) start();
          else mql.addEventListener('change', start, { once: true });
        }

        async load() {
          const url = `https://apid.juniphq.com/en/v2/products/remote/${this.dataset.product}/reviews?page_size=40&sort_field=created_at&sort_order=desc`;
          const res = await fetch(url, { headers: { 'junip-store-key': this.dataset.key, 'Content-Type': 'application/json' } });
          if (!res.ok) return;
          const { data = [] } = await res.json();
          // Skip reviews that aren't about the product itself (not used yet, shipping) or that mention prices/sales,
          // which go stale.
          const skip = /(haven'?t|have not|havent|not yet|yet to|can'?t wait|cant wait|will update|arrived|shipping|delivery|package|order|refund|return|wish|but |however|only complaint|unfortunately|\$\s?\d|\bsale\b|black friday|discount|coupon|price|cheap)/i;
          const clean = (t) => String(t || '').replace(/\s+/g, ' ').trim();
          const picks = [];
          for (const r of data) {
            if (r.rating !== 5 || r.verified_buyer === false) continue;
            const body = clean(r.body);
            if (body.length < 25 || body.length > 170 || skip.test(body)) continue;
            if (picks.some((p) => p.body.toLowerCase() === body.toLowerCase())) continue;
            const name = clean(r.customer && (r.customer.first_name || r.customer.name)) || '';
            picks.push({ body, name: name.split(' ')[0] });
            if (picks.length >= Number(this.dataset.count || 2)) break;
          }
          if (!picks.length) return;
          const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          this.innerHTML =
            '<ul class="grq__list" role="list">' +
            picks.map((p) =>
              `<li class="grq__item"><span class="grq__stars" aria-label="5 out of 5 stars">★★★★★</span>` +
              `<q class="grq__text">${esc(p.body)}</q>` +
              `<span class="grq__by">${p.name ? esc(p.name) + ' · ' : ''}Verified buyer</span></li>`
            ).join('') +
            '</ul><a class="grq__all" href="#reviews" data-gph-rating-link>Read all reviews</a>';
          this.hidden = false;
          this.querySelector('[data-gph-rating-link]').addEventListener('click', (e) => {
            const hit = document.querySelector('[id*="junip_product_review" i], [id*="junip-product-review" i], .junip-product-review, junip-product-review');
            const target = hit ? hit.closest('.shopify-section') || hit : null;
            if (!target) return;
            e.preventDefault();
            scrollToEl(target);
          });
        }
      }
    );
  }
})();
